'use client';

/**
 * Small presentational primitives shared across the kids module.
 *
 * They exist so the same stat tile, rail and section header are literally the
 * same component everywhere — the fastest way to keep a large surface visually
 * consistent is to make inconsistency require extra work.
 */
import React from 'react';
import Link from 'next/link';

/* ───────────────────────────────────────────────────────────── stat tile ── */

export function StatTile({
  label,
  value,
  sub,
  emoji,
  color = '--k-grape',
  className = '',
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  emoji?: string;
  color?: string;
  className?: string;
}) {
  return (
    <div className={`k-card p-4 flex items-center gap-3 ${className}`}>
      {emoji && (
        <span
          className="grid place-items-center rounded-2xl shrink-0"
          style={{
            width: 44,
            height: 44,
            fontSize: 22,
            background: `color-mix(in srgb, var(${color}) 14%, transparent)`,
          }}
          aria-hidden="true"
        >
          {emoji}
        </span>
      )}
      <div className="min-w-0">
        <div className="k-eyebrow">{label}</div>
        <div
          className="font-extrabold leading-none mt-1"
          style={{ fontSize: '1.55rem', fontVariantNumeric: 'tabular-nums' }}
        >
          {value}
        </div>
        {sub && <div className="text-xs text-muted-foreground mt-1 truncate">{sub}</div>}
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────── rail ── */

export function ProgressRail({
  value,
  max = 100,
  label,
  showValue = false,
  color,
}: {
  value: number;
  max?: number;
  label?: string;
  showValue?: boolean;
  color?: string;
}) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div>
      {(label || showValue) && (
        <div className="flex items-baseline justify-between mb-1.5">
          {label && <span className="k-eyebrow">{label}</span>}
          {showValue && (
            <span className="text-xs font-extrabold text-muted-foreground tabular-nums">
              {Math.round(value)} / {max}
            </span>
          )}
        </div>
      )}
      <div
        className="k-rail"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label ?? 'Progress'}
      >
        <div
          className="k-rail-fill"
          style={{ width: `${pct}%`, background: color ? `var(${color})` : undefined }}
        />
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── pills ── */

export function Pill({
  children,
  color = '--k-grape',
  soft = true,
  className = '',
}: {
  children: React.ReactNode;
  color?: string;
  soft?: boolean;
  className?: string;
}) {
  return (
    <span
      className={`k-pill ${className}`}
      style={
        soft
          ? {
              background: `color-mix(in srgb, var(${color}) 14%, transparent)`,
              color: `color-mix(in srgb, var(${color}) 82%, var(--foreground))`,
            }
          : { background: `var(${color})`, color: '#fff' }
      }
    >
      {children}
    </span>
  );
}

export function CoinBadge({ coins, size = 'md' }: { coins: number; size?: 'sm' | 'md' }) {
  return (
    <span
      className="k-pill"
      style={{
        background: 'var(--k-mango-soft)',
        color: 'color-mix(in srgb, var(--k-mango) 80%, var(--foreground))',
        fontSize: size === 'sm' ? '0.75rem' : undefined,
      }}
      aria-label={`${coins} coins`}
    >
      <span aria-hidden="true">🪙</span>
      <span className="tabular-nums">{coins.toLocaleString()}</span>
    </span>
  );
}

/* ─────────────────────────────────────────────────────── section heading ── */

export function SectionHead({
  eyebrow,
  title,
  sub,
  action,
  as: Heading = 'h2',
}: {
  eyebrow?: string;
  title: React.ReactNode;
  sub?: React.ReactNode;
  action?: React.ReactNode;
  as?: 'h1' | 'h2' | 'h3';
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
      <div className="min-w-0">
        {eyebrow && <span className="k-eyebrow">{eyebrow}</span>}
        <Heading
          className="mt-1"
          style={{
            fontSize:
              Heading === 'h1'
                ? 'clamp(1.9rem,1.2rem+2.6vw,2.75rem)'
                : 'clamp(1.25rem,1rem+1vw,1.6rem)',
          }}
        >
          {title}
        </Heading>
        {sub && <p className="text-muted-foreground mt-2 max-w-2xl">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────── empty state ─ */

export function EmptyState({
  emoji,
  title,
  body,
  href,
  cta,
}: {
  emoji: string;
  title: string;
  body: string;
  href?: string;
  cta?: string;
}) {
  return (
    <div className="k-card-lg p-8 text-center">
      <div style={{ fontSize: 48 }} aria-hidden="true">
        {emoji}
      </div>
      <h3 className="mt-3 text-xl">{title}</h3>
      <p className="text-muted-foreground mt-2 max-w-md mx-auto">{body}</p>
      {href && cta && (
        <Link href={href} className="k-btn k-btn-primary mt-5">
          {cta}
        </Link>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── skeletons ── */

/**
 * Rendered while the saved profile is being read. It matches the real layout's
 * height so the page does not jump — a layout shift here would count against
 * Cumulative Layout Shift on every kids route.
 */
export function LoadingBlock({
  height = 200,
  label = 'Loading your progress',
}: {
  height?: number;
  label?: string;
}) {
  return (
    <div
      className="k-card grid place-items-center text-sm font-bold text-muted-foreground"
      style={{ minHeight: height }}
      role="status"
      aria-live="polite"
    >
      {label}…
    </div>
  );
}
